import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { useConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog/useConfirmDialog';
import { Switch } from 'src/core/interim/Switch';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useSyncCenterSettingsWithMutation } from 'src/hooks/api/useSyncCenterSettingsWithMutation';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, SaleClient } from 'src/WebApiController';

import * as styles from './AccountSetting.css';
import { RevealSyncDateInput } from './RevealSyncDateInput';
export { divider } from '../../Settings.css';

export function AccountingSetting() {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasEditRevealSyncDateFeature = useUserHasFeature(
    Feature.EditRevealSyncDate
  );
  const hasAutoLinkSaleLineItemFeature = useUserHasFeature(
    Feature.AutoLinkSaleLineItemSetting
  );
  const hasPurchaseOverPayLogicBehindSettingFeature = useUserHasFeature(
    Feature.PurchaseOverPayLogicBehindSetting
  );

  const { query, mutation } = useSyncCenterSettingsWithMutation();

  const {
    openConfirmDialog: openConfirmDeleteDialog,
    closeConfirmDialog: closeConfirmDeleteDialog,
  } = useConfirmDialog();

  const hasBackfillCancelledProceedsFeature = useUserHasFeature(
    Feature.BackfillCancelledProceeds
  );

  if (query.isLoading) {
    return <PosSpinner />;
  }

  return (
    <div
      className={styles.sectionContainer}
      style={{ gap: vars.spacing['xl'] }}
    >
      <h5 className={styles.sectionHeading}>
        <Content id={ContentId.CancelledOrders} />
      </h5>
      <Stack direction="column" gap="m">
        <div className={styles.label}>
          <Content id={ContentId.CancelledProceeds} />
        </div>
        <p className={styles.subtleText}>
          <Content id={ContentId.CancelledProceedsSettingDescription} />
        </p>
        <Stack direction="row" gap="m" alignItems="center">
          <Switch
            disabled={mutation.isPending}
            checked={query.data?.isAutoAddCancelledProceedsEnabled ?? false}
            onChange={(e) => {
              mutation.mutate({
                isAutoAddCancelledProceedsEnabled: e.currentTarget.checked,
              });

              if (
                hasBackfillCancelledProceedsFeature &&
                e.currentTarget.checked
              ) {
                openConfirmDeleteDialog({
                  size: 's',
                  headerText: (
                    <Content id={ContentId.BackfillCancelledProceeds} />
                  ),
                  bodyText: (
                    <Content id={ContentId.BackfillCancelledProceedsWarning} />
                  ),
                  onOkay: () => {
                    new SaleClient(
                      activeAccountWebClientConfig
                    ).backfillCancelledProceeds();
                    closeConfirmDeleteDialog();
                  },
                  onCancel: closeConfirmDeleteDialog,
                  okText: ContentId.Yes,
                  cancelText: ContentId.No,
                });
              }
            }}
          />
          <div className={styles.label}>
            <Content id={ContentId.CancelledProceedsSettingToggle} />
          </div>
        </Stack>
      </Stack>
      {hasAutoLinkSaleLineItemFeature && (
        <>
          <div className={styles.divider} />
          <h5 className={styles.sectionHeading}>
            <Content id={ContentId.Sales} />
          </h5>
          <Stack direction="column" gap="m">
            <div className={styles.label}>
              <Content id={ContentId.SaleLineItems} />
            </div>
            <p className={styles.subtleText}>
              <Content id={ContentId.AutoLinkSaleLineItemDescription} />
            </p>
            <Stack direction="row" gap="m" alignItems="center">
              <Switch
                disabled={mutation.isPending}
                checked={query.data?.isAutoLinkSaleLineItemEnabled ?? false}
                onChange={(e) => {
                  mutation.mutate({
                    isAutoLinkSaleLineItemEnabled: e.currentTarget.checked,
                  });
                }}
              />
              <div className={styles.label}>
                <Content id={ContentId.AutoLinkSaleLineItemToggle} />
              </div>
            </Stack>
          </Stack>
        </>
      )}
      {hasPurchaseOverPayLogicBehindSettingFeature && (
        <>
          <div className={styles.divider} />
          <h5 className={styles.sectionHeading}>
            <Content id={ContentId.Cost} />
          </h5>
          <Stack direction="column" gap="m">
            <p className={styles.subtleText}>
              <Content id={ContentId.UpdateUnitCostsForOverpaymentDetails} />
            </p>
            <Stack direction="row" gap="m" alignItems="center">
              <Switch
                disabled={mutation.isPending}
                checked={query.data?.includeOverPaymentForUnitCost ?? false}
                onChange={(e) => {
                  mutation.mutate({
                    includeOverPaymentForUnitCost: e.currentTarget.checked,
                  });
                }}
              />
              <div className={styles.label}>
                <Content id={ContentId.UpdateUnitCostsForOverpayment} />
              </div>
            </Stack>
          </Stack>
        </>
      )}
      {hasEditRevealSyncDateFeature && (
        <>
          <div className={styles.divider} />
          <RevealSyncDateInput />
        </>
      )}
    </div>
  );
}
